<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations } from '$lib/utils/animations';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import PDFViewer from '$lib/components/PDFViewer.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';

  let scrollY = 0;
  let translateY = 0;
  $: translateY = scrollY * 0.5;

  const pdfImages = [
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-0.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-3.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-8.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-18.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-20.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-21.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-24.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-25.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-27.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-30.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-31.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-30.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-31.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-35.jpg',
    '/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-36.jpg',
  ];

  onMount(() => {
    setupProjectAnimations();
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Design for Decisions"
    client="Studio NAND"
    clientLogo=""
    industry="Design"
    product="Strategy Case Study"
    heroMedia={{
      type: 'image',
      src: '/projects/design-for-decisions/dfd-cover-hand.jpg'
    }}
    metrics={[
      { value: 200, label: "Students Impacted" },
      { value: 3, label: "Semesters" },
      { value: 5, label: "Workshops" }
    ]}
    roles={["Creative Lead – responsible for content structure & strategy"]}
  />

  <div class="project-section">
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90 animate-on-view">
        How to Navigate Decisions is a strategic lead magnet created to reframe Studio NAND's design practice.
      </p>
    </div>

    <ChallengeSolution
      challengeText="Studio NAND had long built tools that help people understand complexity — but lacked a unifying story to describe our value."
      solutionTitle="Value Delivered"
      solutionText="Positioned NAND as a creative and strategic partner in decision-making. Sparked conversations with clients in health, policy, and tech — and laid the foundation for how we now communicate our work."
    />

    <section class="animate-on-view">
      <div class="container">
        <div class="grid grid-cols-1 gap-4">
          <div class="mt-8 py-6">
            <p>
              Over more than a decade, Studio NAND built interfaces that help people understand complexity — in health, mobility, and emerging tech. But we lacked a coherent external narrative for what we actually do. That changed during a strategic collaboration with <a href="https://basicarts.org/" target="_blank" class="underline">Basic Arts</a>, where we discovered the unifying principle behind our work: Design for Decision.

              This lead magnet was initiated to articulate and test this new positioning — not as a brand statement, but as something useful, generous, and aligned with our values. The challenge: How do we distill years of experience into a concise format that speaks directly to leaders and practitioners facing real-world decisions?
            </p>
          </div>
        </div>
      </div>
    </section>

    <div class="relative w-full overflow-hidden mt-16 mb-16 animate-on-view" data-delay="7">
      <FullWidthImage
        src="/projects/design-for-decisions/dfd-cover.jpg"
        alt="Design for Decisions Interface"
        caption="The lead magnet became the foundation for Studio NAND's repositioning around “Design for Decision” — influencing our website, pitch materials, and client conversations. Most of the content was authored by myself."
      />
    </div>

    <section class="animate-on-view">
      <div class="container">
        <div class="grid">
          <div class="relative w-full overflow-hidden animate-on-view">
            <h2>Content Strategy</h2>
            <ul class="mt-4 list-disc list-inside">
              <li>Modular chapters reflecting different decision-making modes (data-based, intuitive, AI-assisted)</li>
              <li>Clear, editorial tone that avoids buzzwords and invites engagement</li>
              <li>Visual storytelling designed for scanning or deep reading</li>
              <li>A bridge between thought leadership and practical framing</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="animate-on-view mt-24">
      <div class="w-full mb-32">
        <h2 class="text-2xl font-light mt-8 mb-8 px-8">Key Features</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-8">
          <FeatureCell
            icon="school"
            title="Decision Frameworks"
            description="Structured around real-world decision types: intuitive, data-based, AI-assisted."
          />
          <FeatureCell
            icon="group"
            title="Useful"
            description="Combines practical value with clear positioning for Studio NAND."
          />
          <FeatureCell
            icon="insights"
            title="Editorial Voice"
            description="Clear, approachable tone — no jargon."
          />
          <FeatureCell
            icon="devices"
            title="Toolkits Included"
            description="Curated decision-making and foresight tools to take action."
          />
        </div>
      </div>
    </section>
    <section class="animate-on-view">
      <div class="container">
        <div class="grid grid-cols-1 gap-4">
          <div class="mt-8 py-6">
            <p>
              How to Navigate Decisions is a strategic lead magnet created to reframe Studio NAND's design practice around a single unifying idea: Design for Decision. The piece translates over a decade of work into a clear, useful guide for decision-makers — blending design strategy, data thinking, and AI insights into a narrative that's both practical and positioning.
            </p>
          </div>
        </div>
      </div>
    </section>

    <div class="relative w-full overflow-hidden mt-16 mb-16 animate-on-view" data-delay="7">
      <div class="grid grid-cols-5 gap-4">
        <img 
          src="/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-5.jpg" 
          alt="Design for Decisions Interface" 
          class="w-full h-full object-cover"
        />
        <img 
          src="/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-14.jpg" 
          alt="Design for Decisions Interface" 
          class="w-full h-full object-cover"
        />
        <img 
          src="/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-20.jpg" 
          alt="Design for Decisions Interface" 
          class="w-full h-full object-cover"
        />
        <img 
          src="/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-25.jpg" 
          alt="Design for Decisions Interface" 
          class="w-full h-full object-cover"
        />
        <img 
          src="/projects/design-for-decisions/Studio NAND – How to Navigate Decisions-bilder-30.jpg" 
          alt="Design for Decisions Interface" 
          class="w-full h-full object-cover"
        />
      </div>
      <div class="mt-8 border-t border-b border-black/10 py-6 text-center">
        <p class="text-sm text-gray-500">
          I created a visual identity for each chapter using generative design tools. 
        </p>
      </div>
    </div>
  </div>
  <ProjectNav 
    prevProject={{ title: "Is This Thing On?", url: "/projects/is-this-thing-on" }}
    nextProject={{ title: "Framing Art Science Technology", url: "/projects/framing-art-science-technology" }}
  />
  <Footer />
</div> 